import * as React from "react";
import { Route, Switch, Redirect, BrowserRouter as Router } from "react-router-dom";
import "./App.css";
import Login from "./pages/Login/Login";
import PrivacyPolicy from "./pages/PrivacyPolicy/PrivacyPolicy"
import SignUp from "./pages/SignUp/SignUp";
import ConfirmEmail from "./pages/ConfirmEmail/ConfirmEmail"
import Home from "./pages/Home";
import OAuthRedirect from "./pages/OAuthRedirect";
import PrivateRoute from "./containers/PrivateRoute";
import ForgotPassword from "./pages/ForgotPassword/ForgotPassword";
import ResetPassword from "./pages/ResetPassword/ResetPassword";
import BackendStatus from "./components/BackendStatus";
import AnnouncementBar from "./components/AnnouncementBar/AnnouncementBar";
import { getAppStoreLink, getPlatformCTAKey } from "./utils/platformDetection";
import ReactGA from "react-ga4";
import { Suspense } from "react";
import { Dimmer, Loader } from "semantic-ui-react";
import { initializeFirebase, firebaseConfig } from "./config/firebase";
import { api } from "./config/axiosConfig";
import BluetoothProvider from "./contexts/BluetoothContext";
import { withTranslation, WithTranslation } from 'react-i18next';
import './i18n';

function initializeReactGA() {
      ReactGA.initialize("G-XXXXXXXXXX"); // Update with your GA4 measurement ID
  ReactGA.send({ hitType: "pageview", page: "/" });
}

// Initialize Firebase
initializeFirebase();

// Export firebaseConfig for backward compatibility
export { firebaseConfig };
// Analytics Integration
initializeReactGA();

const LoadingSpinner = () => (
    <Dimmer active style={{ height: "100%" }}>
        <Loader size='big' style={{ display: "block" }} />
    </Dimmer>
);

interface AppState {
    backendOnline: boolean;
    appStoreLink: string;
    appStoreCTA: string;
    announcementMessage: string;
}

class App extends React.Component<WithTranslation, AppState> {
    state: AppState = {
        backendOnline: false, // Start with false to show offline status
        appStoreLink: '',
        appStoreCTA: '',
        announcementMessage: ''
    };

    componentDidMount() {
        // Simple check to see if backend is reachable
        this.checkBackendStatus();
        
        // Set up mobile app announcement based on platform
        this.setupMobileAppAnnouncement();
    }
    
    componentDidUpdate(prevProps: WithTranslation) {
        // Update announcement if language changes
        if (prevProps.i18n.language !== this.props.i18n.language) {
            this.setupMobileAppAnnouncement();
        }
    }
    
    setupMobileAppAnnouncement = () => {
        const { t } = this.props;
        const appStoreLinks = {
            ios: 'https://apps.apple.com/ca/app/entralpi-climbing-training/id6504147881',
            android: 'https://play.google.com/store/apps/details?id=com.entralpi.entralpi'
        };
        
        const link = getAppStoreLink(appStoreLinks);
        
        // Get translated message
        const message = `📱 ${t('mobileApp.message')}`;
        
        // Get platform-specific translated CTA
        const ctaKey = getPlatformCTAKey();
        const cta = t(ctaKey);
        
        this.setState({
            appStoreLink: link,
            appStoreCTA: cta,
            announcementMessage: message
        });
    };

    checkBackendStatus = async () => {
        try {
            // Use the configured API system instead of direct fetch
            const response = await api().get('/v1/health');
            if (response.status === 200) {
                this.setState({ backendOnline: true });
            } else {
                this.setState({ backendOnline: false });
            }
        } catch (error) {
            console.log('Backend appears to be offline:', error);
            this.setState({ backendOnline: false });
        }
    };

    render() {   
        return (
            <BluetoothProvider>
                <Router>
                    {/* AnnouncementBar - Mobile app announcement with platform detection */}
                    {this.state.announcementMessage && (
                        <AnnouncementBar
                            id="entralpi-mobile-app-2025-11"
                            message={this.state.announcementMessage}
                            linkText={this.state.appStoreCTA}
                            linkHref={this.state.appStoreLink}
                            color="linear-gradient(90deg, #667eea, #764ba2)"
                        />
                    )}
                    
                    <Suspense fallback={<LoadingSpinner />}>
                        <BackendStatus isOnline={this.state.backendOnline} />
                        <Switch>
                            <Route exact path="/login" component={Login} />
                            <Route exact path="/privacy" component={PrivacyPolicy} />
                            <Route exact path="/signup/register" component={SignUp} />
                            <Route exact path="/signup/confirm/email" component={ConfirmEmail}/>
                            <Route exact path="/oauth/redirect" component={OAuthRedirect} />
                            <Route exact path="/account/recovery" component={ForgotPassword} />
                            <Route exact path="/account/reset" component={ResetPassword} />
                            <Route exact path="/app" render={() => <Redirect to="/" />} />
                            <PrivateRoute path="/" component={Home}/>
                            <Route render={() => <Redirect to="/login" />} />
                        </Switch>
                    </Suspense>
                </Router>
            </BluetoothProvider>
        );
    }
}

export default withTranslation('common')(App);
